import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import he from 'he';

// CareerDetailsPage.jsx
// Usage: <CareerDetailsPage id={123} />
// Requirements: tailwindcss, framer-motion, axios

export default function CareerDetails() {
	const { id } = useParams(); 
	const [career, setCareer] = useState(null);
	const [lists, setLists] = useState([]);
	const [activeTab, setActiveTab] = useState("All");
	const [loading, setLoading] = useState(true);
	const [openError, setOpenError] = useState(null);
	
	const [error, setError] = useState(null);
	const careerPost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,				
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/career", payload);
			
			// Loop data (array)
			setLists(response.data || []);			
		} catch (err) {
			console.log(err);
		    setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	const CareerDetails = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				id: id,
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/career-details", payload);
			console.log(response.data);
			// Loop data (array)
			setCareer(response.data[0]);			
		} catch (err) {
			console.log(err);
		    setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {		
		careerPost();
		CareerDetails();
	}, []);
	
	const academicJobs = lists? lists.filter((job) => job.job_type === "Academic")  : [];
	const nonAcademicJobs = lists? lists.filter((job) => job.job_type === "Non Academic")  : [];
	const generalStaffJobs = lists? lists.filter((job) => job.job_type === "General Staff")  : [];

	function formatDate(iso) {
		if (!iso) return "-";
		try {
		  const d = new Date(iso);
		  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
		} catch (e) {
		  return iso;
		}
	}
	const tabData = {
		All: lists,
		Academic: academicJobs,
		"Non Academic": nonAcademicJobs,
		"General Staff": generalStaffJobs,
	};
	function decodetitle(textdata){
		let textinfo="";
		textinfo=textdata.replace(/\\n/g, '<br />');
		textinfo=textinfo.split('\\n').join('\n');
		textinfo=textinfo.split('\n').join('<br />');
		textinfo=textinfo.split('\\r').join(' ');
		textinfo=textinfo.split('\\').join(' ');
		
		
		textinfo = he.decode(textinfo); 
		return textinfo;
	}
  return (
    <section className="min-h-screen bg-gradient-to-b from-[#016C94] to-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* HERO */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-2xl p-6 md:p-10 mb-8"
        >
          {/* Decorative SVG background */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 1000 300"
            preserveAspectRatio="none"
            aria-hidden
          >
            <defs>
              <linearGradient id="g1" x1="0%" x2="100%">
                <stop offset="0%" stopColor="#0ea5a4" stopOpacity="0.12" />
                <stop offset="100%" stopColor="#6366f1" stopOpacity="0.08" />
              </linearGradient>
            </defs>
            <rect width="100%" height="100%" fill="url(#g1)" />
            <g transform="translate(0,40)" opacity="0.55">
              <path d="M0,160 C150,40 350,260 500,150 C650,40 850,260 1000,140 L1000,300 L0,300 Z" fill="#fff" />
            </g>
          </svg>

          <div className="relative flex flex-col md:flex-row gap-6 items-center">
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight">
                Career Details
              </h1>
              <p className="mt-2 text-slate-600 max-w-2xl">
                Detailed view of an open position with qualification, publication policy and application timeline.
              </p>
            </div>

            <div className="w-full md:w-64 text-right">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-indigo-600 text-white shadow-lg text-sm font-semibold"
                onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })}
              >
                See Open Careers
              </motion.button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* LEFT: Career Details */}
          <motion.article
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="md:col-span-2 bg-white rounded-2xl shadow p-6"
            aria-live="polite"
          >
            {loading && <p className="text-slate-500">Loading career...</p>}
            {!loading && !career && <p className="text-red-500">No career found for id: {id}</p>}

            {career && (
              <div>
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">{career.designation_name}</h2>
                    <p className="text-sm text-slate-500">{career.department_name}</p>
                    <div className="mt-3 inline-flex items-center gap-3">
                      <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-slate-100">{career.job_type}</span>
                      <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-slate-100">Position: {career.position_number || '-'}</span>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-sm text-slate-500">Open: <strong>{formatDate(career.open_date)}</strong></p>
                    <p className="text-sm text-rose-600">Deadline: <strong>{formatDate(career.deadline)}</strong></p>
                    <motion.a
                      whileHover={{ scale: 1.03 }}
                      className="mt-3 inline-block px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold shadow"
                      href={`/dashboard/apply/${id}`}
                    >
                      Apply Now
                    </motion.a>
                  </div>
                </div>

                <hr className="my-5" />

                <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                  <div>
                    <h3 className="font-semibold text-slate-800">Subject</h3>
                    <p className="mt-1 text-slate-600">{career.subject || '-'}</p>

                    <h3 className="mt-4 font-semibold text-slate-800">Qualification</h3>
                    <div className="mt-2 text-slate-700 whitespace-pre-line text-sm"
                       dangerouslySetInnerHTML={{ __html: decodetitle(career.qualification)}} >
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-800">Additional Requirement</h3>
					 <div className="mt-2 text-slate-700 whitespace-pre-line text-sm"
                      dangerouslySetInnerHTML={{ __html: decodetitle(career.additional_requirement)}}>
                    </div>
                    

                    {career.responsibilites && (
                      <>
                        <h3 className="mt-4 font-semibold text-slate-800">Responsibilities</h3>
						<div className="mt-2 text-slate-700 whitespace-pre-line text-sm"
						  dangerouslySetInnerHTML={{ __html: decodetitle(career.responsibilites)}}>
						</div>
                        
                      </>
                    )}

                    {career.additional_note && (
                      <>
                        <h3 className="mt-4 font-semibold text-slate-800">Note</h3>
						<div className="mt-2 text-slate-700 whitespace-pre-line text-sm"
						  dangerouslySetInnerHTML={{ __html: decodetitle(career.additional_note)}}>
						</div>
                        
                      </>
                    )}
                  </div>
                </div>

                
              </div>
            )}
          </motion.article>

          {/* RIGHT: Open Career List with category tabs */}
          <motion.aside
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="md:col-span-1 bg-white rounded-2xl shadow p-4"
          >
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-slate-900">Open Careers</h4>
              <span className="text-sm text-slate-500">{lists ? lists.length : 0}</span>
            </div>

            <div className="mt-3 flex gap-2 flex-wrap">
              {['All','Academic','Non Academic','General Staff'].map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveTab(t)}
                  className={`px-3 py-1 text-sm rounded-md font-medium ${activeTab===t? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                >
                  {t}
                </button>
              ))}
            </div>

            <div className="mt-4  overflow-auto pr-2">
              {openError && <p className="text-red-500">{openError}</p>}
              {!lists && <p className="text-slate-500">Loading open list...</p>}

              {tabData[activeTab] && tabData[activeTab].length === 0 && (
                <p className="text-slate-500 text-sm">No items in this category.</p>
              )}

              <ul className="space-y-3">
                {(tabData[activeTab] || []).map((it, idx) => (
                  <li key={idx} className="p-3 rounded-lg border border-slate-100 hover:shadow-sm transition">
                    <a href={`/career/${it.id || it.sl || idx}`} className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{it.designation_name || it.subject || 'Untitled'}</div>
                        <div className="text-xs text-slate-500">{it.department_name}</div>
                        <div className="text-xs text-slate-400 mt-1">{it.job_type} • Open: {formatDate(it.open_date)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-rose-600">{formatDate(it.deadline)}</div>
                        <div className="text-xs text-slate-400">Positions: {it.position_number || '-'}</div>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 text-center">
              <a href="/" className="inline-block text-sm underline text-slate-600">View all careers</a>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
